import { Button, IconButton } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import type { ComponentPropsWithoutRef } from "react";
import { useState } from "react";
import type UIColumnSuggestion from "../../models/UIColumnSuggestion";
import ColumnSuggestionsList from "../ColumnSuggestionsList/ColumnSuggestionsList";
import { StyledFooter, StyledMainContent, StyledSubtitle, StyledTitle, StyledTooltipContentWrapper } from "./styles";

type Props = {
    className?: string;
    columnsSuggestions: readonly UIColumnSuggestion[];
    onApplyClick(columnNames: readonly string[]): void;
    onCancelClick(): void;
} & ComponentPropsWithoutRef<"div">;

const ColumnSuggestionsTooltipContent = ({
    className,
    columnsSuggestions,
    onApplyClick,
    onCancelClick,
    ...htmlElementProps
}: Props) => {
    const [selectedSuggestions, setSelectedSuggestions] = useState<readonly string[]>(getInitialSelectedSuggestions);

    function getInitialSelectedSuggestions(): readonly string[] {
        return columnsSuggestions.flatMap(({ field, isNew }) => (isNew ? [field] : []));
    }

    function handleCheckboxClick(columnName: string): void {
        const newSuggestions = new Set(selectedSuggestions);

        if (newSuggestions.has(columnName)) {
            newSuggestions.delete(columnName);
        } else {
            newSuggestions.add(columnName);
        }

        setSelectedSuggestions(Array.from(newSuggestions));
    }

    function handleSuggestionsApplyClick(): void {
        onApplyClick(selectedSuggestions);
    }

    return (
        <StyledTooltipContentWrapper
            className={className}
            {...htmlElementProps}
        >
            <I18n
                element={StyledTitle}
                translatable={{ key: "si.column-suggestions.title" }}
            />
            <I18n
                element={StyledSubtitle}
                translatable={{ key: "si.column-suggestions.description" }}
            />

            <StyledMainContent>
                <ColumnSuggestionsList
                    selectedColumnsNames={selectedSuggestions}
                    columnSuggestions={columnsSuggestions}
                    onColumnSelect={handleCheckboxClick}
                />
            </StyledMainContent>

            <StyledFooter>
                <IconButton
                    type="outlined"
                    iconName="add"
                    isDisabled={selectedSuggestions.length === 0}
                    onClick={handleSuggestionsApplyClick}
                >
                    {translate("si.column-suggestions.button.apply")}
                </IconButton>

                <Button
                    type="flat"
                    onClick={onCancelClick}
                >
                    {translate("si.column-suggestions.button.select-columns")}
                </Button>
            </StyledFooter>
        </StyledTooltipContentWrapper>
    );
};

export default ColumnSuggestionsTooltipContent;
